import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { formatLocalTime } from "../utils/playbackHelper";
import { clampTrimRange, formatTrimDuration } from "../utils/trimRange";

const QUALITY_OPTIONS = [
  { value: "SD", label: "SD" },
  { value: "HD", label: "HD" },
];
const BAND_TOP = 32;
const BAND_BOTTOM = 23;
const HANDLE_WIDTH = 8;

const clampPercent = (value) => Math.min(100, Math.max(0, value));

const TrimSelector = ({
  timelineRef,
  timelineStart,
  timeWindow,
  range,
  maxTime,
  exporting = false,
  progress = 0,
  onRangeChange,
  onSelectQuality,
  onCancelExport,
}) => {
  const [qualityAnchorEl, setQualityAnchorEl] = useState(null);
  const dragEdgeRef = useRef(null);
  const blockRef = useRef(null);

  const toPercent = (sec) => ((sec * 1000 - timelineStart) / timeWindow) * 100;
  const startPercent = toPercent(range.startTime);
  const endPercent = toPercent(range.endTime);
  const left = clampPercent(startPercent);
  const right = clampPercent(endPercent);
  if (right <= left) return null;

  const secondsAtClientX = (clientX) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect?.width) return null;
    const ratio = (clientX - rect.left) / rect.width;
    return (timelineStart + ratio * timeWindow) / 1000;
  };

  const stop = (event) => event.stopPropagation();

  const handleEdgePointerDown = (edge) => (event) => {
    event.stopPropagation();
    if (exporting) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragEdgeRef.current = edge;
  };

  const handleEdgePointerMove = (event) => {
    event.stopPropagation();
    const edge = dragEdgeRef.current;
    if (!edge) return;
    const seconds = secondsAtClientX(event.clientX);
    if (seconds === null) return;
    const next = clampTrimRange(edge, seconds, range, { max: maxTime });
    if (next.startTime !== range.startTime || next.endTime !== range.endTime) onRangeChange(next);
  };

  const handleEdgePointerUp = (event) => {
    event.stopPropagation();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragEdgeRef.current = null;
  };

  const openQuality = (event) => {
    event.stopPropagation();
    if (exporting) return;
    setQualityAnchorEl(blockRef.current);
  };

  const handleSelectQuality = (quality) => {
    setQualityAnchorEl(null);
    onSelectQuality(quality);
  };

  const renderHandle = (edge, visible) => visible && (
    <Box
      role="slider"
      aria-label={edge === "start" ? "Trim start" : "Trim end"}
      aria-valuenow={edge === "start" ? range.startTime : range.endTime}
      onPointerDown={handleEdgePointerDown(edge)}
      onPointerMove={handleEdgePointerMove}
      onPointerUp={handleEdgePointerUp}
      onPointerCancel={handleEdgePointerUp}
      onClick={stop}
      sx={{
        position: "absolute",
        top: -1,
        bottom: -1,
        [edge === "start" ? "left" : "right"]: -HANDLE_WIDTH / 2,
        width: HANDLE_WIDTH,
        borderRadius: "2px",
        backgroundColor: "#172664",
        cursor: exporting ? "default" : "ew-resize",
        touchAction: "none",
        zIndex: 1,
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",
          top: "30%",
          bottom: "30%",
          borderLeft: "1px solid rgba(255,255,255,0.8)",
        },
      }}
    />
  );

  return (
    <>
      <Typography
        sx={{
          position: "absolute",
          left: `${(left + right) / 2}%`,
          top: 16,
          transform: "translateX(-50%)",
          zIndex: 5,
          fontSize: 10,
          fontWeight: 700,
          lineHeight: "14px",
          color: "#172664",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {`${formatLocalTime(new Date(range.startTime * 1000), true)} – ${formatLocalTime(new Date(range.endTime * 1000), true)}`}
        {` (${formatTrimDuration(range.endTime - range.startTime)})`}
        {exporting && ` · ${Math.round(progress * 100)}%`}
      </Typography>
      <Box
        ref={blockRef}
        onPointerDown={stop}
        onPointerMove={stop}
        onClick={openQuality}
        sx={{
          position: "absolute",
          left: `${left}%`,
          width: `${right - left}%`,
          top: BAND_TOP,
          bottom: BAND_BOTTOM,
          zIndex: 5,
          backgroundColor: "rgba(23, 38, 100, 0.32)",
          borderTop: "1px solid #172664",
          borderBottom: "1px solid #172664",
          cursor: exporting ? "progress" : "pointer",
        }}
      >
        {exporting && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${Math.round(progress * 100)}%`,
              backgroundColor: "rgba(23, 38, 100, 0.35)",
              pointerEvents: "none",
            }}
          />
        )}
        {renderHandle("start", startPercent >= 0)}
        {renderHandle("end", endPercent <= 100)}
        <Tooltip title={exporting ? "Cancel download" : "Download"}>
          <IconButton
            size="small"
            aria-label={exporting ? "Cancel download" : "Download clip"}
            onClick={(event) => {
              event.stopPropagation();
              if (exporting) onCancelExport();
              else openQuality(event);
            }}
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 22,
              height: 22,
              p: 0,
              zIndex: 2,
              color: "#172664",
              backgroundColor: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "#f0f4fa" },
            }}
          >
            {exporting ? (
              <>
                <CircularProgress
                  variant="determinate"
                  value={Math.round(progress * 100)}
                  size={22}
                  thickness={4}
                  sx={{ position: "absolute", color: "#172664" }}
                />
                <CloseIcon sx={{ fontSize: 12 }} />
              </>
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={Boolean(qualityAnchorEl) && !exporting}
        anchorEl={qualityAnchorEl}
        onClose={() => setQualityAnchorEl(null)}
        onPointerDown={stop}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              mb: 1,
              borderRadius: "6px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              border: "1px solid #e0e0e0",
              overflow: "hidden",
              minWidth: 58,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
          {QUALITY_OPTIONS.map((option) => (
            <Box
              key={option.value}
              role="button"
              onClick={() => handleSelectQuality(option.value)}
              sx={{
                px: 1.5,
                py: 0.4,
                fontSize: 12,
                textAlign: "center",
                cursor: "pointer",
                color: "#333",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

TrimSelector.propTypes = {
  timelineRef: PropTypes.object.isRequired,
  timelineStart: PropTypes.number.isRequired,
  timeWindow: PropTypes.number.isRequired,
  range: PropTypes.shape({
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number.isRequired,
  }).isRequired,
  maxTime: PropTypes.number,
  exporting: PropTypes.bool,
  progress: PropTypes.number,
  onRangeChange: PropTypes.func.isRequired,
  onSelectQuality: PropTypes.func.isRequired,
  onCancelExport: PropTypes.func.isRequired,
};

export default TrimSelector;
